/**
 * Oracle Chat Availability Logic
 * Determines if the chat widget should be visible based on business hours.
 *
 * Configuration is REQUIRED from Contentful "Chat Availability" content type.
 * Requires pages to have a chatAvailability reference linked.
 *
 * Contentful Chat Availability fields:
 * - timeZone (string): timezone (e.g., "America/Los_Angeles")
 * - businessDays (string): Comma-separated weekday numbers (0-6, where 0=Sunday)
 * - startTime (string): HH:MM format in 24-hour time (e.g., "05:00")
 * - endTime (string): HH:MM format in 24-hour time (e.g., "17:00")
 * - closedDates (JSON string): Array of manual closure dates (e.g., '["2026-12-25"]')
 */

const pad2 = (value) => String(value).padStart(2, '0');
const dateKey = (year, month, day) => `${year}-${pad2(month)}-${pad2(day)}`;

/**
 * Convert a Date to specific timezone's date parts
 */
function datePartsInTimeZone(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-US-u-ca-gregory', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
    weekday: 'short',
  }).formatToParts(date);

  const values = Object.fromEntries(
    parts
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value])
  );

  const weekdayNumber = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  }[values.weekday];

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second),
    weekday: weekdayNumber,
  };
}

/**
 * Normalize Contentful chat availability data to expected format
 * @param {Object} contentfulData - Data from Contentful Chat Availability entry
 * @returns {Object} - Normalized schedule object
 * @throws {Error} - If required fields (timeZone, businessDays, startTime, endTime) are missing or null
 */
export function normalizeChatSchedule(contentfulData) {
  if (
    !contentfulData?.timeZone ||
    !contentfulData?.businessDays ||
    !contentfulData?.startTime ||
    !contentfulData?.endTime
  ) {
    throw new Error(
      'Oracle Chat Widget configuration is incomplete. ' +
        'Required fields: timeZone, businessDays, startTime, endTime'
    );
  }

  let extraClosedDates = [];
  if (contentfulData.closedDates) {
    try {
      if (typeof contentfulData.closedDates === 'string') {
        const trimmed = contentfulData.closedDates.trim();
        if (trimmed) {
          extraClosedDates = JSON.parse(trimmed);
        }
      } else if (Array.isArray(contentfulData.closedDates)) {
        extraClosedDates = contentfulData.closedDates;
      }
    } catch (err) {
      extraClosedDates = [];
    }
  }

  return {
    timeZone: contentfulData.timeZone,
    businessDays: contentfulData.businessDays.split(',').map(Number),
    startTime: contentfulData.startTime,
    endTime: contentfulData.endTime,
    extraClosedDates,
  };
}

/**
 * Check if chat is open based on provided schedule
 * @param {Object} schedule - Schedule object (required, from normalizeChatSchedule)
 * @param {Date} now - Date to check (defaults to current time)
 * @returns {boolean} - True if chat should be visible
 * @throws {Error} - If schedule is not provided
 */
// eslint-disable-next-line import/prefer-default-export
export function isWithinBusinessHours(schedule, now = new Date()) {
  if (!schedule) {
    throw new Error(
      'Schedule is required for isWithinBusinessHours. ' +
        'Call normalizeChatSchedule(contentfulData) first.'
    );
  }

  const current = datePartsInTimeZone(now, schedule.timeZone);
  const currentDate = dateKey(current.year, current.month, current.day);
  const currentTimeInMinutes = current.hour * 60 + current.minute;
  const [startHour, startMinute] = schedule.startTime.split(':').map(Number);
  const [endHour, endMinute] = schedule.endTime.split(':').map(Number);
  const startTimeInMinutes = startHour * 60 + startMinute;
  const endTimeInMinutes = endHour * 60 + endMinute;

  if (!schedule.businessDays.includes(current.weekday)) {
    return false;
  }

  const isWithinHours =
    currentTimeInMinutes >= startTimeInMinutes &&
    currentTimeInMinutes < endTimeInMinutes;
  if (!isWithinHours) {
    return false;
  }

  if (schedule.extraClosedDates.includes(currentDate)) {
    return false;
  }

  return true;
}
